import {
  Badge,
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  toast,
  Tooltip,
} from "@bleu.builders/ui";
import { CheckIcon, LockClosedIcon } from "@radix-ui/react-icons";
import React from "react";

import { useUser } from "#/contexts/UserContext";
import {
  DialogTemplatedMessage,
  TemplateMessage,
} from "#/components/CreateEmailLink";

import { ToggleNotificationPreference } from "./ThesesItemNotificationPreference";
import { isRecentlyCreated } from "./ThesesTableBody";
import { ThesisDetails } from "./ThesisDetails";

const BlockedIcon = () => (
  <Tooltip
    content="Apenas teses básicas estão disponiveis para assinantes do plano
      Experimente."
  >
    <LockClosedIcon className="size-4 text-primary" />
  </Tooltip>
);

const PurchasedIcon = () => (
  <Tooltip content="Você já adquiriu essa tese.">
    <CheckIcon className="size-4 text-primary" />
  </Tooltip>
);

export function ThesisItem({
  row,
  index,
  parentRowIndex,
  defaultOpen = false,
}) {
  const [isExpanded, setIsExpanded] = React.useState(defaultOpen);

  const { user } = useUser();
  const notAvailableForTheUser =
    user.subscription?.status === "trialing" &&
    !user.subscription?.trial_premium &&
    !row.original.is_basic &&
    !row.original.organization_purchased;

  const notifyNotAvailable = () => {
    toast({
      title: "Tese não disponível",
      description: (
        <div>
          Apenas teses básicas estão disponíveis para assinantes do plano
          Experimente.{" "}
          <DialogTemplatedMessage
            template={TemplateMessage.TRIAL_SUBSCRIBE}
            firstName={user.first_name}
            email={user.email}
          >
            <Button
              variant="link"
              className="p-0 m-0 text-white h-auto underline text-xs"
            >
              Clique aqui
            </Button>
          </DialogTemplatedMessage>{" "}
          para falar com nosso time e ter acesso a todas as teses.
        </div>
      ),
      variant: "destructive",
    });
  };

  const isRecently = isRecentlyCreated(row.original.published_at);

  return (
    <Collapsible
      key={row.id}
      open={isExpanded && !notAvailableForTheUser}
      onOpenChange={
        notAvailableForTheUser
          ? () => notifyNotAvailable()
          : (isOpen) => {
              setIsExpanded(isOpen);
            }
      }
    >
      <CollapsibleTrigger className="w-full select-text">
        <div
          className={cn(
            "px-2 py-3 bg-muted dark:border-2 flex justify-between",
            {
              "border-t rounded-t-md border-b-2 text": isExpanded,
              "rounded-md border": !isExpanded,
            },
          )}
        >
          <span className="text-base font-normal flex items-center text-left">
            <ToggleNotificationPreference
              row={row}
              index={index}
              parentRowIndex={parentRowIndex}
            />
            <span className="p-2">{row.original.title}</span>
          </span>
          <div className="flex flex-row space-x-2 items-center pr-2">
            {row.original.is_in_update && (
              <Badge className="bg-orange-500 hover:bg-orange-400">
                Em Atualização
              </Badge>
            )}
            {row.original.has_update && (
              <Badge className="bg-orange-500 hover:bg-orange-400">
                Atuallização Disponível
              </Badge>
            )}
            {row.original.superior_court_impact &&
              row.original.superior_court_impact.map((impact) => (
                <Badge key={impact}>{impact}</Badge>
              ))}
            {row.original.decision === "Oportunidades Administrativas" && (
              <Badge>Oportunidade Administrativa</Badge>
            )}
            {notAvailableForTheUser && <BlockedIcon />}
            {isRecently &&
              !row.original.organization_purchased &&
              !row.original.archived && (
                <Badge className="bg-green-600/50 hover:bg-green-600/40">
                  Tese nova
                </Badge>
              )}
            {row.original.archived && (
              <Badge className="bg-muted-foreground/60 hover:bg-muted-foreground/40">
                Arquivada
              </Badge>
            )}
            {row.original.organization_purchased && <PurchasedIcon />}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ThesisDetails defaultOpen={defaultOpen} row={row} />
      </CollapsibleContent>
    </Collapsible>
  );
}
